/**
 * Sub-aba "Cliente Comunidade": cria o login de portal de uma cliente já atendida que nunca usou o
 * sistema, e ela recebe a senha provisória por e-mail. Porte de `admin.html:2182-2193` e
 * `7438-7468`; linhas 125 e 126 do inventário.
 *
 * ENTRA COM ESTE NOME por decisão do fundador em 19/09/2026 (achado 9 do inventário): o nome cita a
 * comunidade, mas o mecanismo é só a Edge Function `criar-cliente-portal`, sem tabela `comunidade_*`.
 *
 * DIFERENÇAS DE COR DO STATUS, iguais ao legado: validação e andamento em cinza, erro em vermelho
 * (e o erro não some), sucesso em cinza sumindo em 5 s se ainda for o mesmo texto.
 */

// A memória de função do React (o estado desta sub-aba mora na memória da área).
import { useCallback } from 'react';
// As peças da plataforma.
import { Botao, Campo } from '@complexity/ui';
// A chamada de `criar-cliente-portal`, com o corpo do legado (`admin.html:7455`).
import { criarAcessoClienteDoPortal } from '@studiolu/dados/areas/sistema';
// Sessão e fonte de dados.
import { usePainel } from '../../contexto.js';
// A memória da área, e o status que some em 5 s com o texto guardado nela (`admin.html:7467`).
import { useMemoria, useStatusDaArea } from './memoria.js';
// A máscara do WhatsApp ao digitar, já portada por Vendas (`aplicarMascaraWhatsapp`, `admin.html:6497`).
import { aplicarMascaraWhatsapp } from '../vendas/regras.js';
// A linha compacta dos campos curtos.
import { LinhaCompacta } from '../financeiro/apoio.js';
// As peças visuais da área.
import { NotaSecao } from './pecas.js';

export function SubAbaClienteComunidade() {
  const { dados } = usePainel();
  // Os três campos, todos exigidos no clique, na memória da área: no legado são `input` fixos da
  // página (`admin.html:2184-2190`), e ir a outra aba e voltar encontra o que foi digitado.
  const [nome, setNome] = useMemoria('sistema:cliente-comunidade:nome', '');
  const [whatsapp, setWhatsapp] = useMemoria('sistema:cliente-comunidade:whatsapp', '');
  const [email, setEmail] = useMemoria('sistema:cliente-comunidade:email', '');
  // O botão fica travado enquanto a função trabalha (`admin.html:7453` e `7456`). Também na memória:
  // a função continua respondendo depois de a tela sair, e é ela que destrava o botão.
  const [criando, setCriando] = useMemoria('sistema:cliente-comunidade:criando', false);
  // O texto do status: `mostrar` some em 5 s, `fixar` fica. O parágrafo é fixo (`admin.html:2192`).
  const [status, mostrarStatus, fixarStatus] = useStatusDaArea('sistema:cliente-comunidade:status', 5000);
  // Vermelho só no erro da função (`status.style.color = 'var(--erro)'`), e o legado não o desfaz
  // ao trocar de aba: a cor fica no elemento até o próximo clique em "Criar acesso".
  const [emErro, setEmErro] = useMemoria('sistema:cliente-comunidade:em-erro', false);

  // "Criar acesso" (`admin.html:7442-7468`).
  const criarAcesso = useCallback(async () => {
    // Cada clique volta o status ao cinza (`admin.html:7445`).
    setEmErro(false);
    const nomeLimpo = nome.trim();
    const whatsappLimpo = whatsapp.trim();
    const emailLimpo = email.trim();
    if (!nomeLimpo || !whatsappLimpo || !emailLimpo) {
      fixarStatus('Preencha nome, WhatsApp e e-mail.');
      return;
    }
    // O mesmo teste de e-mail do legado: algo, arroba, algo, ponto, algo, sem espaço.
    if (!/^\S+@\S+\.\S+$/.test(emailLimpo)) {
      fixarStatus('E-mail inválido.');
      return;
    }
    setCriando(true);
    fixarStatus('Criando acesso...');
    const resultado = await criarAcessoClienteDoPortal(dados, { nome: nomeLimpo, whatsapp: whatsappLimpo, email: emailLimpo });
    setCriando(false);
    // Falha da chamada, ou `error` no corpo: "Erro: " e o motivo, em vermelho (`admin.html:7457-7460`).
    // O legado lê `data?.error || error.message`: fora de 2xx o `data` vem nulo, e vale a mensagem genérica.
    if (!resultado.ok || resultado.dados?.error) {
      setEmErro(true);
      fixarStatus('Erro: ' + (resultado.ok ? resultado.dados?.error : resultado.erro.mensagem));
      return;
    }
    // Deu certo: a frase do legado, ou o aviso que a função mandou; campos limpos; some em 5 s.
    const aviso = resultado.dados?.aviso;
    mostrarStatus(aviso ? `Acesso criado, mas: ${aviso}` : 'Acesso criado! A cliente já recebeu o e-mail com a senha provisória.');
    setNome('');
    setWhatsapp('');
    setEmail('');
  }, [nome, whatsapp, email, dados, mostrarStatus, fixarStatus, setNome, setWhatsapp, setEmail, setCriando, setEmErro]);

  return (
    <div className="flex flex-col gap-cx-4">
      <NotaSecao>
        Cria um login de portal do zero pra uma cliente que já foi atendida mas nunca usou o sistema, sem passar pelo Funil, só
        pra ela poder entrar e (se quiser) participar da Comunidade. Cada cliente recebe uma{' '}
        <strong>senha provisória própria, sorteada na hora</strong>, junto com as instruções de acesso, por e-mail automático.
        Você não precisa (e não consegue) ver essa senha aqui: se ela não receber o e-mail, o caminho é &quot;Esqueci minha
        senha&quot; no portal. No primeiro login o portal pergunta se ela quer trocar a senha.
      </NotaSecao>
      <LinhaCompacta>
        <Campo
          rotulo="Nome"
          type="text"
          autoComplete="name"
          value={nome}
          onChange={(evento) => setNome(evento.target.value)}
        />
        {/* 15 caracteres: "(00) 00000-0000", o tamanho da máscara completa. */}
        <Campo
          rotulo="WhatsApp"
          type="text"
          autoComplete="tel"
          inputMode="tel"
          placeholder="(00) 00000-0000"
          maxLength={15}
          value={whatsapp}
          onChange={(evento) => setWhatsapp(aplicarMascaraWhatsapp(evento.target.value))}
        />
        <Campo
          rotulo="E-mail"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(evento) => setEmail(evento.target.value)}
        />
        <Botao disabled={criando} onClick={() => void criarAcesso()}>
          Criar acesso
        </Botao>
      </LinhaCompacta>
      {/* O parágrafo `#cliente-comunidade-status`, cinza ou vermelho conforme o legado pinta. */}
      <p
        role="status"
        data-testid="status-cliente-comunidade"
        className={`min-h-[1.25rem] text-cx-sm [overflow-wrap:anywhere] ${emErro ? 'text-erro' : 'text-texto-suave'}`}
      >
        {status}
      </p>
    </div>
  );
}
